<template>
  <div class="ai-float-container" v-if="isLoggedIn">
    <!-- 悬浮图标 -->
    <div class="ai-float-icon" @click="toggleChatDialog" v-show="!dialogVisible">
      <i class="el-icon-chat-dot-round"></i>
    </div>

    <!-- 聊天对话框 -->
    <el-dialog
      title="AI助手"
      :visible.sync="dialogVisible"
      width="80%"
      :before-close="handleClose"
      custom-class="ai-chat-dialog"
    >
      <div class="chat-container">
        <!-- 全屏加载遮罩 -->
        <div v-if="loading && deepThinkMode" class="loading-overlay">
          <div class="loading-content">
            <div class="loading-spinner">
              <i class="el-icon-loading"></i>
            </div>
            <div class="loading-text">{{ deepThinkMode ? 'AI 正在深度思考中...' : 'AI 正在思考中...' }}</div>
            <div class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div class="loading-tip">请稍候，正在为您生成回答</div>
          </div>
        </div>
        <!-- 聊天历史记录 -->
        <div class="chat-history" ref="chatHistory">
          <div v-if="chatMessages.length === 0" class="empty-chat">
            <i class="el-icon-s-opportunity"></i>
            <p>您可以向AI助手提问任何问题</p>
          </div>
          <div v-else>
            <div v-for="(message, index) in chatMessages" :key="index" class="message-item" :class="message.type">
              <div class="message-avatar">
                <i :class="message.type === 'user' ? 'el-icon-user' : 'el-icon-chat-dot-round'"></i>
              </div>
              <div class="message-content" v-if="message.type === 'user'">{{ message.content }}</div>
              <!-- 例题做题模式 -->
              <div class="message-content ai-content quiz-mode" v-else-if="message.isQuiz">
                <div class="quiz-question" v-html="formatMessage(message.questionPart)"></div>
                <div class="quiz-options">
                  <div
                    v-for="opt in ['A', 'B', 'C', 'D', 'E']"
                    :key="opt"
                    class="quiz-option"
                    :class="{
                      'selected': message.userAnswer === opt,
                      'correct': message.showAnswer && opt === message.correctAnswer,
                      'wrong': message.showAnswer && message.userAnswer === opt && opt !== message.correctAnswer
                    }"
                    @click="!message.showAnswer && selectQuizAnswer(index, opt)"
                  >
                    {{ opt }}
                  </div>
                </div>
                <div v-if="!message.showAnswer" class="quiz-submit">
                  <el-button type="primary" size="small" @click="submitQuizAnswer(index)" :disabled="!message.userAnswer">
                    提交答案
                  </el-button>
                </div>
                <div v-else class="quiz-result">
                  <div :class="message.userAnswer === message.correctAnswer ? 'result-correct' : 'result-wrong'">
                    {{ message.userAnswer === message.correctAnswer ? '✓ 回答正确！' : '✗ 回答错误' }}
                  </div>
                  <div class="quiz-answer-section" v-html="formatMessage(message.answerPart)"></div>
                </div>
              </div>
              <!-- 普通AI消息渲染 -->
              <div
                v-else-if="message.type === 'ai' && message.isStreaming"
                class="message-content ai-content streaming-plain"
              >{{ message.content }}</div>
              <div
                class="message-content ai-content"
                v-else
                v-html="formatMessage(message.content)"
                :class="{'thinking-process': message.isThinkingProcess}"
              ></div>
            </div>
          </div>

          <!-- 知识点快捷操作按钮（放到空白态也可见） -->
          <div v-if="showKnowledgeActions" class="quick-actions">
            <div class="quick-actions-title">快捷操作：</div>
            <el-button size="small" type="primary" @click="generateExampleQuestions(currentKnowledgeName)" :disabled="loading">
              <i class="el-icon-document"></i> 生成例题
            </el-button>
            <el-button size="small" type="success" @click="explainKnowledge(currentKnowledgeName)" :disabled="loading">
              <i class="el-icon-reading"></i> 详细讲解
            </el-button>
          </div>
        </div>

        <!-- 输入区域 -->
        <div class="chat-input-area">
          <div class="input-options">
            <el-switch
              v-model="deepThinkMode"
              active-text="深度思考模式"
              inactive-text="普通模式"
              active-color="#13ce66"
              @change="handleModeChange"
            ></el-switch>
            <el-tooltip v-if="deepThinkMode" effect="dark" content="使用deepseek-r1:1.5b模型进行更深入的思考" placement="top">
              <i class="el-icon-info model-info"></i>
            </el-tooltip>
          </div>
          <div v-if="loading && !deepThinkMode" class="streaming-hint">AI 正在生成中...</div>
          <el-input
            v-model="userInput"
            type="textarea"
            :rows="3"
            placeholder="请输入您的问题..."
            @keydown.enter.native.prevent="handleEnterKey"
          ></el-input>
          <div class="input-actions">
            <span class="input-tip">按 Enter 发送，Ctrl+Enter 换行</span>
            <el-button type="primary" @click="sendMessage" :loading="loading" :disabled="loading">
              <i :class="deepThinkMode ? 'el-icon-cpu' : 'el-icon-s-promotion'"></i>
              {{ loading ? '思考中...' : '发送' }}
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { textCompletion, textCompletionStream, deepThinkCompletion } from '@/api/ai'
import { getToken } from '@/utils/auth' // 导入getToken方法
import { marked } from 'marked' // 修改导入方式
import hljs from 'highlight.js' // 添加highlight.js导入
import 'highlight.js/styles/atom-one-dark.css' // 添加样式导入

export default {
  name: 'AiChatFloat',
  data() {
    return {
      dialogVisible: false,
      userInput: '',
      chatMessages: [],
      loading: false,
      isLoggedIn: false, // 添加登录状态变量
      deepThinkMode: false, // 深度思考模式开关
      pageContext: null, // 当前页面上下文
      hasShownWelcome: false, // 是否已显示欢迎语
      knowledgeContext: null, // 当前知识点上下文（响应式）
      questionContext: null, // 当前题目上下文（响应式）
      formulaContext: null // 当前公式上下文（响应式）
    }
  },
  computed: {
    // 是否显示知识点快捷操作按钮
    showKnowledgeActions() {
      const path = this.$route?.path || ''
      // 使用响应式的 knowledgeContext
      return (path.includes('/amc-knowledge') || path.includes('/knowledge-practice')) && this.knowledgeContext && this.knowledgeContext.name
    },
    // 当前知识点名称
    currentKnowledgeName() {
      return this.knowledgeContext ? this.knowledgeContext.name : ''
    }
  },
  created() {
    // 初始化时检查登录状态
    this.checkLoginStatus()

    // 监听路由变化，更新登录状态和重置欢迎语
    this.$router.afterEach(() => {
      this.checkLoginStatus()
      // 路由变化时重置欢迎语状态，下次打开会根据新页面生成问候语
      this.hasShownWelcome = false
    })
  },
  mounted() {
    // 添加复制代码的全局函数
    if (!window.copyCodeToClipboard) {
      window.copyCodeToClipboard = function(button) {
        const codeBlock = button.parentNode.nextElementSibling
        const code = codeBlock.textContent

        // 创建临时textarea元素
        const textarea = document.createElement('textarea')
        textarea.value = code
        textarea.style.position = 'fixed'
        document.body.appendChild(textarea)
        textarea.select()

        try {
          // 执行复制命令
          document.execCommand('copy')
          button.innerHTML = '<i class="el-icon-check"></i> 已复制'

          // 2秒后恢复原始状态
          setTimeout(() => {
            button.innerHTML = '<i class="el-icon-document-copy"></i> 复制'
          }, 2000)
        } catch (err) {
          console.error('复制失败:', err)
          button.innerHTML = '<i class="el-icon-close"></i> 复制失败'
        }

        document.body.removeChild(textarea)
      }
    }

    // 加载highlight.js的样式
    this.loadHighlightStyles()

    // 注册全局打开AI助手的方法
    window.__openAiAssistant = () => {
      this.dialogVisible = true
      // 更新公式上下文
      this.formulaContext = this.getFormulaContext()
      // 更新知识点上下文
      this.knowledgeContext = this.getKnowledgeContext()
      // 更新题目上下文
      this.questionContext = this.getQuestionContext()
      // 清空聊天记录，重新生成问候语
      this.chatMessages = []
      this.hasShownWelcome = false
      this.generateContextWelcome()
    }

    // 监听自定义事件
    window.addEventListener('openAiAssistant', () => {
      if (window.__openAiAssistant) {
        window.__openAiAssistant()
      }
    })
  },

  methods: {
    // 处理Enter键
    handleEnterKey(event) {
      // 如果按下Ctrl键，则允许换行
      if (event.ctrlKey) {
        // 在光标位置插入换行符
        const textarea = event.target
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const value = textarea.value

        // 更新文本内容，在光标位置插入换行符
        this.userInput = value.substring(0, start) + '\n' + value.substring(end)

        // 下一个事件循环中设置光标位置
        this.$nextTick(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 1
        })
      } else {
        // 如果没有按Ctrl，则发送消息
        this.sendMessage()
      }
    },

    // 检查用户是否已登录
    checkLoginStatus() {
      // 获取token判断是否登录
      const token = getToken()
      // 判断当前路由是否为登录或注册页面
      const currentPath = this.$route.path
      const isLoginPage = currentPath === '/login' || currentPath === '/register'

      // 只有在有token且不在登录页面时才显示AI助手
      this.isLoggedIn = !!token && !isLoginPage
    },
    toggleChatDialog() {
      this.dialogVisible = !this.dialogVisible
      // 打开对话框时，根据上下文生成智能问候语
      if (this.dialogVisible) {
        // 更新知识点上下文（响应式）
        this.knowledgeContext = this.getKnowledgeContext()
        // 更新题目上下文（响应式）
        this.questionContext = this.getQuestionContext()

        // 如果聊天记录为空或只有欢迎消息，重新生成问候语
        const onlyWelcome = this.chatMessages.length === 1 && this.chatMessages[0].isWelcome
        if (this.chatMessages.length === 0 || onlyWelcome) {
          if (onlyWelcome) {
            this.chatMessages = [] // 清空旧的欢迎消息
          }
          this.generateContextWelcome()
        }
      }
    },

    // 根据页面上下文生成智能问候语
    generateContextWelcome() {
      const route = this.$route
      const path = route.path
      const query = route.query

      let welcomeMessage = ''

      // 获取页面标题用于辅助判断
      const pageTitle = route.meta?.title || document.title || ''

      // 获取各种上下文
      const questionContext = this.questionContext || this.getQuestionContext()

      // 根据不同页面生成不同的问候语（优先匹配更具体的路径）
      // 首先检查是否有公式上下文（说明从公式页面点击求助）
      const formulaContext = this.formulaContext || this.getFormulaContext()

      if (formulaContext && formulaContext.formula) {
        // 有公式上下文，根据操作类型生成不同的问候语
        const actionType = formulaContext.actionType
        const category = formulaContext.category || '数学公式'
        if (actionType === 'explain') {
          welcomeMessage = `您好！我来为您讲解这个公式。\n\n**知识点：** ${category}\n**公式：** $${formulaContext.formula}$\n\n我将从以下几个方面为您详细讲解：\n1. 公式的含义和推导\n2. 适用条件和注意事项\n3. 典型应用场景\n\n请稍等，正在为您生成详细讲解...`
          // 自动发送讲解请求
          this.$nextTick(() => {
            this.autoSendFormulaRequest('explain', formulaContext)
          })
        } else if (actionType === 'practice') {
          welcomeMessage = `您好！我来为您生成关于这个公式的练习题。\n\n**知识点：** ${category}\n**公式：** $${formulaContext.formula}$\n\n请稍等，正在为您生成练习题...`
          // 自动发送生成练习题请求
          this.$nextTick(() => {
            this.autoSendFormulaRequest('practice', formulaContext)
          })
        } else {
          welcomeMessage = `您好！我注意到您正在查看公式。\n\n**知识点：** ${category}\n**公式：** $${formulaContext.formula}$\n\n请问您想要：\n- 详细讲解这个公式\n- 生成相关练习题\n- 了解公式的应用场景`
        }
        // 清除公式上下文，避免重复触发
        this.clearFormulaContext()
      } else if (questionContext && questionContext.content) {
        // 有题目上下文，说明在刷题页面
        const contentPreview = questionContext.content.length > 100
          ? questionContext.content.substring(0, 100) + '...'
          : questionContext.content
        welcomeMessage = `您好！我注意到您正在练习题目。\n\n**当前题目：**\n${contentPreview}\n\n请问您对这道题有什么疑问吗？比如：\n- 这道题的解题思路是什么？\n- 某个选项为什么对/错？\n- 涉及到哪些知识点？`
      } else if (path.includes('/quick-practice') || path.includes('/daily-question') || path.includes('/knowledge-question') || pageTitle.includes('今日任务') || pageTitle.includes('快速练习') || pageTitle.includes('每日一题')) {
        // 刷题页面（无题目上下文时）
        welcomeMessage = '您好！我注意到您正在刷题练习。请问有什么可以帮助您的吗？比如：\n- 对某道题目有疑问\n- 想了解某个知识点\n- 需要解题思路指导'
      } else if (path.includes('/amc-past-papers') || pageTitle.includes('历年真题')) {
        // 历年真题页面
        welcomeMessage = '您好！我注意到您正在练习AMC历年真题。请问有什么可以帮助您的吗？比如：\n- 对某道真题有疑问\n- 想了解某年真题的难度分布\n- 需要某类题型的解题技巧'
      } else if (path.includes('/wrong-questions') || pageTitle.includes('错题')) {
        // 错题本
        welcomeMessage = '您好！我注意到您正在查看错题本。请问有什么可以帮助您的吗？比如：\n- 分析某道错题的原因\n- 相关知识点讲解\n- 类似题型的练习建议'
      } else if (path.includes('/consecutive-challenge') || pageTitle.includes('连对挑战')) {
        // 连续挑战
        welcomeMessage = '您好！我注意到您正在进行连续挑战。请问有什么可以帮助您的吗？比如：\n- 快速解题技巧\n- 时间管理建议\n- 某道题目的疑问'
      } else if (path.includes('/amc-knowledge') || path.includes('/knowledge-practice') || pageTitle.includes('知识库') || pageTitle.includes('知识点')) {
        // 知识库页面
        const knowledgeCtx = this.getKnowledgeContext()
        if (knowledgeCtx && knowledgeCtx.name) {
          welcomeMessage = `您好！我注意到您正在浏览知识库。\n\n**当前选中知识点：** ${knowledgeCtx.name}\n\n您可以点击下方快捷按钮，或直接向我提问：\n- 这个知识点的详细讲解\n- 生成相关例题练习\n- 与其他知识点的关联`
        } else {
          welcomeMessage = '您好！我注意到您正在浏览知识库。\n\n请先点击选择一个知识点，然后可以使用快捷按钮生成例题或获取详细讲解。\n\n您也可以直接向我提问任何数学问题！'
        }
      } else if (path === '/amc-course' || (path.includes('/amc-course') && !questionContext)) {
        // AMC备考中心（仅当路径精确匹配或没有题目上下文时）
        welcomeMessage = '您好！欢迎来到AMC备考中心。请问有什么可以帮助您的吗？比如：\n- 如何制定备考计划\n- 各模块功能介绍\n- AMC考试相关问题'
      } else if (path === '/' || path.includes('/dashboard') || path.includes('/home')) {
        // 主页/仪表盘
        welcomeMessage = '您好！欢迎使用智能学习助手。请问有什么可以帮助您的吗？比如：\n- 了解各功能模块\n- 学习建议\n- 使用帮助'
      } else {
        // 其他页面
        welcomeMessage = '您好！我是您的AI学习助手，有什么可以帮助您的吗？'
      }

      // 添加AI欢迎消息
      if (welcomeMessage && this.chatMessages.length === 0) {
        this.chatMessages.push({
          type: 'ai',
          content: welcomeMessage,
          isWelcome: true
        })
      }
    },

    // 获取当前题目上下文（从全局状态或事件总线）
    getQuestionContext() {
      // 尝试从 window 全局变量获取当前题目信息
      if (window.__currentQuestionContext) {
        return window.__currentQuestionContext
      }
      // 尝试从 localStorage 获取
      try {
        const ctx = localStorage.getItem('currentQuestionContext')
        if (ctx) {
          return JSON.parse(ctx)
        }
      } catch (e) {
        console.warn('获取题目上下文失败:', e)
      }
      return null
    },
    // 获取当前公式上下文
    getFormulaContext() {
      if (window.__currentFormulaContext) {
        return window.__currentFormulaContext
      }
      try {
        const ctx = localStorage.getItem('currentFormulaContext')
        if (ctx) {
          return JSON.parse(ctx)
        }
      } catch (e) {
        console.warn('获取公式上下文失败:', e)
      }
      return null
    },
    // 清除公式上下文
    clearFormulaContext() {
      window.__currentFormulaContext = null
      this.formulaContext = null
      try {
        localStorage.removeItem('currentFormulaContext')
      } catch (e) {}
    },
    // 自动发送公式相关请求
    autoSendFormulaRequest(actionType, formulaContext) {
      const formula = formulaContext.formula
      const category = formulaContext.category || '数学公式'

      let prompt = ''
      if (actionType === 'explain') {
        prompt = `请详细讲解以下数学公式：\n\n【知识点】${category}\n【公式】$${formula}$\n\n请从以下几个方面进行讲解：\n1. 公式的含义和各符号的意义\n2. 公式的推导过程（如适用）\n3. 公式的适用条件和注意事项\n4. 2-3个典型的应用例子\n\n请使用LaTeX格式书写数学公式。`
      } else if (actionType === 'practice') {
        prompt = `请根据以下公式生成3道练习题：\n\n【知识点】${category}\n【公式】$${formula}$\n\n要求：\n1. 难度由易到难递进\n2. 每道题都要有详细的解答过程\n3. 题目要能够帮助学生理解和掌握这个公式的应用\n4. 使用选择题格式，每题4个选项\n\n请使用LaTeX格式书写数学公式，并按照以下格式输出：\n【题目1】\n题目内容\nA. 选项A\nB. 选项B\nC. 选项C\nD. 选项D\n【答案】X\n【解析】详细解析过程`
      }

      if (prompt) {
        // 模拟用户发送消息
        this.userInput = prompt
        this.sendMessage()
      }
    },
    // 获取当前知识点上下文
    getKnowledgeContext() {
      if (window.__currentKnowledgeContext) {
        return window.__currentKnowledgeContext
      }
      try {
        const ctx = localStorage.getItem('currentKnowledgeContext')
        if (ctx) {
          return JSON.parse(ctx)
        }
      } catch (e) {
        console.warn('获取知识点上下文失败:', e)
      }
      return null
    },
    // 快捷操作：根据知识点生成例题
    generateExampleQuestions(knowledgeName) {
      const prompt = `请根据【${knowledgeName}】这个知识点，生成1道AMC竞赛风格的例题，要求：
1. 包含题目、选项（A-E五个选项）、答案和详细解析
2. 解析要说明解题思路和涉及的知识点
3. 使用LaTeX格式书写数学公式`

      // 添加用户消息
      this.chatMessages.push({
        type: 'user',
        content: `请根据【${knowledgeName}】知识点生成例题`
      })

      this.loading = true
      this.$nextTick(() => this.scrollToBottom())

      // 调用AI接口，使用专门的例题处理方法
      textCompletion(prompt)
        .then(this.handleQuizResponse)
        .catch(this.handleApiError)
        .finally(() => {
          this.loading = false
        })
    },
    // 处理例题响应，解析为做题模式
    handleQuizResponse(res) {
      if (res.code === 1 && res.data) {
        const content = res.data
        // 解析AI返回的内容，分离题目和答案解析
        const parsed = this.parseQuizContent(content)

        this.chatMessages.push({
          type: 'ai',
          isQuiz: true,
          questionPart: parsed.questionPart,
          answerPart: parsed.answerPart,
          correctAnswer: parsed.correctAnswer,
          userAnswer: null,
          showAnswer: false,
          fullContent: content
        })
        this.$nextTick(() => {
          this.scrollToBottom()
          this.renderMath()
        })
      } else {
        this.handleApiError(res)
      }
    },
    // 解析例题内容
    parseQuizContent(content) {
      // 找到答案部分的位置
      const answerMatch = content.match(/###\s*答案/i) || content.match(/\*\*答案[：:]/i) || content.match(/答案[：:]\s*[A-E]/i)
      const analysisMatch = content.match(/###\s*解析/i) || content.match(/\*\*解析[：:]/i)

      let questionPart = content
      let answerPart = ''
      let correctAnswer = ''

      // 提取正确答案（A-E）
      const answerLetterMatch = content.match(/###\s*答案\s*\n+\s*([A-E])/i) ||
                                content.match(/\*\*答案[：:]\s*([A-E])/i) ||
                                content.match(/答案[：:]\s*([A-E])/i) ||
                                content.match(/---\s*\n+\s*\*\*答案[：:]\s*([A-E])/i)
      if (answerLetterMatch) {
        correctAnswer = answerLetterMatch[1].toUpperCase()
      }

      // 分离题目和答案解析部分
      if (answerMatch) {
        const splitIndex = content.indexOf(answerMatch[0])
        questionPart = content.substring(0, splitIndex).trim()
        answerPart = content.substring(splitIndex).trim()
      } else if (analysisMatch) {
        const splitIndex = content.indexOf(analysisMatch[0])
        questionPart = content.substring(0, splitIndex).trim()
        answerPart = content.substring(splitIndex).trim()
      }

      return { questionPart, answerPart, correctAnswer }
    },
    // 选择答案
    selectQuizAnswer(messageIndex, answer) {
      this.$set(this.chatMessages[messageIndex], 'userAnswer', answer)
    },
    // 提交答案
    submitQuizAnswer(messageIndex) {
      this.$set(this.chatMessages[messageIndex], 'showAnswer', true)
      this.$nextTick(() => {
        this.scrollToBottom()
        this.renderMath()
      })
    },
    // 快捷操作：讲解知识点
    explainKnowledge(knowledgeName) {
      const prompt = `请详细讲解【${knowledgeName}】这个数学知识点，要求：
1. 概念定义和核心要点
2. 相关公式和定理（使用LaTeX格式）
3. 在AMC竞赛中的常见应用
4. 解题技巧和注意事项
5. 举1-2个典型例子说明`

      this.chatMessages.push({
        type: 'user',
        content: `请讲解【${knowledgeName}】知识点`
      })

      this.loading = true
      this.$nextTick(() => this.scrollToBottom())

      textCompletion(prompt)
        .then(this.handleApiResponse)
        .catch(this.handleApiError)
        .finally(() => {
          this.loading = false
        })
    },
    handleClose() {
      this.dialogVisible = false
      // 关闭对话框时清空聊天记录，以便下次打开时根据新上下文生成问候语
      this.chatMessages = []
      this.hasShownWelcome = false
    },
    // 开始新对话
    startNewChat() {
      this.chatMessages = []
      this.hasShownWelcome = false
    },
    // 处理模式切换
    handleModeChange(val) {
      const modeText = val ? '深度思考模式已开启' : '已切换回普通模式'
      const modeType = val ? 'success' : 'info'
      this.$message({
        message: modeText,
        type: modeType,
        duration: 2000
      })
    },
    sendMessage() {
      if (!this.userInput.trim()) {
        this.$message.warning('请输入问题内容')
        return
      }

      // 添加用户消息
      this.chatMessages.push({
        type: 'user',
        content: this.userInput
      })

      const userQuestion = this.userInput
      this.userInput = ''
      this.loading = true

      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })

      // 构建提示词，如果有题目上下文则附带
      let prompt = ''
      const qCtx = this.questionContext || this.getQuestionContext()

      if (qCtx && qCtx.content) {
        // 有题目上下文，构建包含题目信息的prompt
        let contextInfo = `【当前题目】\n${qCtx.content}\n`
        if (qCtx.options && qCtx.options.length > 0) {
          contextInfo += `\n【选项】\n${qCtx.options.map(o => `${o.key}. ${o.value}`).join('\n')}\n`
        }
        if (qCtx.knowledge) {
          contextInfo += `\n【知识点】${qCtx.knowledge}\n`
        }
        prompt = `${contextInfo}\n【用户问题】\n${userQuestion}\n\n请根据上述题目信息回答用户的问题。如果用户问的是解题思路，请详细分析；如果问的是某个选项，请解释为什么对或错。使用LaTeX格式书写数学公式。`
      } else {
        prompt = `请回答以下问题：\n${userQuestion}`
      }

      // 根据模式选择不同的API
      if (this.deepThinkMode) {
        const apiCall = deepThinkCompletion(prompt)
        apiCall
          .then(this.handleApiResponse)
          .catch(this.handleApiError)
          .finally(() => {
            this.loading = false
          })
        return
      }

      // 普通模式：使用流式输出
      const aiMsgIndex = this.chatMessages.length
      this.chatMessages.push({
        type: 'ai',
        content: '',
        isStreaming: true,
        isDeepThink: false
      })

      let buffered = ''
      const flush = () => {
        if (!buffered) return
        const prev = this.chatMessages[aiMsgIndex].content || ''
        this.$set(this.chatMessages[aiMsgIndex], 'content', prev + buffered)
        buffered = ''
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }

      const flushTimer = setInterval(flush, 80)

      textCompletionStream(
        prompt,
        (delta) => {
          buffered += delta
        },
        () => {
          clearInterval(flushTimer)
          flush()
          this.loading = false
          this.$set(this.chatMessages[aiMsgIndex], 'isStreaming', false)
          this.$nextTick(() => {
            this.renderMathFormulas()
          })
        },
        (err) => {
          clearInterval(flushTimer)
          this.loading = false
          this.$set(this.chatMessages[aiMsgIndex], 'isStreaming', false)
          this.handleApiError(err)
        }
      )
    },
    handleApiResponse(response) {
      let aiResponse = ''

      // 处理不同的响应格式
      if (response.code === 1) {
        aiResponse = response.data
      } else if (response.code === 200) {
        aiResponse = response.data || response.result || response.message || ''
      } else if (typeof response === 'string') {
        aiResponse = response
      } else {
        aiResponse = '抱歉，我无法回答这个问题。'
        this.$message.error(response.msg || '请求失败')
      }

      // 处理深度思考模式的回答格式
      if (this.deepThinkMode) {
        // 尝试分离思考过程和最终回答
        const thinkingMatch = aiResponse.match(/思考过程：([\s\S]*?)\n\n最终回答：([\s\S]*)/i)

        if (thinkingMatch && thinkingMatch.length >= 3) {
          const thinkingProcess = thinkingMatch[1].trim()
          const finalAnswer = thinkingMatch[2].trim()

          // 先添加思考过程
          this.chatMessages.push({
            type: 'ai',
            content: `**思考过程**：\n${thinkingProcess}`,
            isThinkingProcess: true,
            isDeepThink: true
          })

          // 再添加最终回答
          this.chatMessages.push({
            type: 'ai',
            content: finalAnswer,
            isDeepThink: true
          })
        } else {
          // 如果没有匹配到预期格式，直接显示完整回答
          this.chatMessages.push({
            type: 'ai',
            content: aiResponse,
            isDeepThink: this.deepThinkMode
          })
        }
      } else {
        // 普通模式直接添加回答
        this.chatMessages.push({
          type: 'ai',
          content: aiResponse,
          isDeepThink: this.deepThinkMode
        })
      }

      // 渲染数学公式
      this.$nextTick(() => {
        this.renderMathFormulas()
      })
    },
    handleApiError(error) {
      console.error('AI 回答失败:', error)

      // 提取更友好的错误信息
      let errorMsg = '抱歉，服务出现了问题'

      if (error.response && error.response.data) {
        errorMsg += ': ' + (error.response.data.msg || error.message || '未知错误')
      } else {
        errorMsg += ': ' + (error.message || '未知错误')
      }

      // 添加错误消息
      this.chatMessages.push({
        type: 'ai',
        content: errorMsg
      })

      this.$message.error(errorMsg)
    },
    formatMessage(content) {
      if (!content) return ''

      try {
        // 配置marked选项和自定义渲染器
        const renderer = new marked.Renderer()

        // 自定义代码渲染
        renderer.code = function(code, language) {
          // 处理数学公式
          if (language === 'latex' || language === 'math' || language === 'tex') {
            return `<div class="math-formula">${code}</div>`
          }

          // 处理代码块
          let highlightedCode = code
          let languageClass = ''

          // 尝试进行代码高亮
          if (language && hljs.getLanguage(language)) {
            try {
              highlightedCode = hljs.highlight(language, code).value
              languageClass = `language-${language}`
            } catch (e) {
              console.error('代码高亮失败:', e)
            }
          }

          // 构建代码块HTML
          return `
            <div class="code-block-wrapper">
              <div class="code-block-header">
                <span class="code-language">${language || 'plaintext'}</span>
                <button class="copy-button" onclick="copyCodeToClipboard(this)">
                  <i class="el-icon-document-copy"></i> 复制
                </button>
              </div>
              <pre class="code-block ${languageClass}"><code>${highlightedCode}</code></pre>
            </div>
          `
        }

        // 配置marked选项
        marked.setOptions({
          renderer: renderer,
          breaks: true,
          gfm: true
        })

        // 预处理Markdown内容，特别处理数学公式部分
        let processedContent = content

        // 1) 在进入 marked 之前先把公式片段替换成HTML容器，避免Markdown吞掉反斜杠
        // 行间公式：$$...$$ 或 \[...\]
        processedContent = processedContent
          .replace(/\$\$([\s\S]+?)\$\$/g, '<div class="block-formula">$1</div>')
          .replace(/\\\[([\s\S]+?)\\\]/g, '<div class="block-formula">$1</div>')

        // 行内公式：$...$ 或 \(...\)
        processedContent = processedContent
          .replace(/\$([^$\n]+?)\$/g, '<span class="inline-formula">$1</span>')
          .replace(/\\\(([\s\S]+?)\\\)/g, '<span class="inline-formula">$1</span>')

        // 应用Markdown转换
        const htmlContent = marked(processedContent)

        // 延迟执行公式渲染
        setTimeout(() => {
          this.renderMathFormulas()
        }, 100)

        return htmlContent
      } catch (e) {
        console.error('Markdown转换失败:', e)
        return content
      }
    },
    renderMathFormulas() {
      if (window.katex) {
        try {
          const formulas = document.querySelectorAll('.inline-formula, .block-formula, .math-formula')

          // 预处理公式内容，统一格式
          formulas.forEach(el => {
            try {
              let formula = (el.textContent || '').trim()

              // 归一化：避免出现\\times导致KaTeX无法识别
              formula = formula.replace(/\\\\/g, '\\')

              // 使用全局公式修复函数
              if (window.fixLatexNotation) {
                formula = window.fixLatexNotation(formula)
              }

              // 使用KaTeX渲染公式
              window.katex.render(formula, el, {
                throwOnError: false,
                displayMode: el.classList.contains('block-formula') || el.classList.contains('math-formula'),
                output: 'html',
                trust: true,
                strict: false
              })
            } catch (error) {
              console.error('KaTeX渲染失败:', error, '公式内容:', el.textContent)
              // 保留原始内容，添加错误样式
              el.classList.add('render-error')
              el.setAttribute('title', '公式渲染失败: ' + error.message)
            }
          })

          // 处理渲染后的位置对齐
          document.querySelectorAll('.inline-formula .katex').forEach(el => {
            el.style.verticalAlign = 'middle'
          })
        } catch (e) {
          console.error('公式渲染过程出错:', e)
        }
      } else {
        console.warn('KaTeX未加载，正在尝试加载...')
        // 尝试动态加载KaTeX
        this.loadKaTeX()
      }
    },
    loadKaTeX() {
      if (window.katex) return // 已加载则直接返回

      const loadScript = (src) => {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = src
          script.onload = resolve
          script.onerror = reject
          document.head.appendChild(script)
        })
      }

      const loadCSS = (href) => {
        return new Promise((resolve, reject) => {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = href
          link.onload = resolve
          link.onerror = reject
          document.head.appendChild(link)
        })
      }

      Promise.all([
        loadCSS('https://unpkg.com/katex@0.16.4/dist/katex.min.css'),
        loadScript('https://unpkg.com/katex@0.16.4/dist/katex.min.js')
      ]).then(() => {
        // 加载扩展
        return Promise.all([
          loadScript('https://unpkg.com/katex@0.16.4/dist/contrib/auto-render.min.js'),
          loadScript('https://unpkg.com/katex@0.16.4/dist/contrib/mhchem.min.js')
        ])
      }).then(() => {
        console.log('KaTeX加载成功')
        // 重新尝试渲染公式
        setTimeout(() => {
          this.renderMathFormulas()
        }, 100)
      }).catch(error => {
        console.error('KaTeX加载失败:', error)
      })
    },
    loadHighlightStyles() {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://unpkg.com/highlight.js@10.7.2/styles/atom-one-dark.min.css'
      document.head.appendChild(link)
    },
    scrollToBottom() {
      if (this.$refs.chatHistory) {
        this.$refs.chatHistory.scrollTop = this.$refs.chatHistory.scrollHeight
      }
    }
  }
}
</script>

<style scoped>
.ai-float-container {
  position: fixed;
  z-index: 9999;
}

.ai-float-icon {
  position: fixed;
  right: 30px;
  bottom: 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #409EFF;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s;
}

.ai-float-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.ai-float-icon i {
  font-size: 28px;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 70vh;
  position: relative;
}

.chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 20px;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
}

.empty-chat i {
  font-size: 48px;
  margin-bottom: 16px;
}

.message-item {
  display: flex;
  margin-bottom: 20px;
}

.message-item.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 12px;
}

.message-item.user .message-avatar {
  background-color: #e6f1fc;
}

.message-item.ai .message-avatar {
  background-color: #ecf5ff;
}

.message-avatar i {
  font-size: 20px;
  color: #409EFF;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.message-item.user .message-content {
  background-color: #409EFF;
  color: white;
}

/* 快捷操作按钮区域 */
.quick-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin: 10px 52px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%);
  border-radius: 8px;
  border: 1px solid #d4e8fc;
}

.quick-actions-title {
  font-size: 13px;
  color: #606266;
  margin-right: 8px;
}

/* 做题模式样式 */
.quiz-mode {
  max-width: 85% !important;
}

.quiz-question {
  margin-bottom: 16px;
}

.quiz-options {
  display: flex;
  gap: 10px;
  margin: 16px 0;
  flex-wrap: wrap;
}

.quiz-option {
  width: 40px;
  height: 40px;
  border: 2px solid #dcdfe6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.quiz-option:hover {
  border-color: #409EFF;
  color: #409EFF;
}

.quiz-option.selected {
  border-color: #409EFF;
  background: #409EFF;
  color: white;
}

.quiz-option.correct {
  border-color: #67c23a;
  background: #67c23a;
  color: white;
}

.quiz-option.wrong {
  border-color: #f56c6c;
  background: #f56c6c;
  color: white;
}

.quiz-submit {
  margin: 16px 0;
}

.quiz-result {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #dcdfe6;
}

.result-correct {
  color: #67c23a;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 12px;
}

.result-wrong {
  color: #f56c6c;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 12px;
}

.quiz-answer-section {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  margin-top: 12px;
}

.ai-content {
  line-height: 1.7;
  font-size: 15px;
  color: #333;
}

.ai-content p {
  margin-bottom: 15px;
}

.ai-content ul,
.ai-content ol {
  padding-left: 20px;
  margin-bottom: 15px;
}

.ai-content h1,
.ai-content h2,
.ai-content h3,
.ai-content h4 {
  margin-top: 20px;
  margin-bottom: 12px;
  color: #1a1a1a;
}

.chat-input-area {
  padding: 10px 0;
}

.input-options {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.model-info {
  margin-left: 8px;
  color: #909399;
  cursor: pointer;
}

.streaming-plain {
  white-space: pre-wrap;
  word-break: break-word;
}

.streaming-plain::after {
  content: '';
  display: inline-block;
  width: 6px;
  height: 1em;
  vertical-align: bottom;
  margin-left: 2px;
  background: rgba(255, 255, 255, 0.6);
  animation: caret-blink 1s step-end infinite;
}

@keyframes caret-blink {
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
}

.input-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.input-tip {
  color: #909399;
  font-size: 12px;
}

/* 数学公式样式 */
.inline-formula {
  display: inline-block;
  margin: 0 2px;
  vertical-align: middle;
}

.block-formula {
  display: block;
  margin: 15px 0;
  text-align: center;
  overflow-x: auto;
  padding: 10px 0;
}

.math-formula {
  display: block;
  margin: 15px 0;
  text-align: center;
  overflow-x: auto;
  padding: 15px 0;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.render-error {
  color: #f56c6c;
  border: 1px dashed #f56c6c;
  padding: 5px;
  margin: 5px 0;
  display: inline-block;
}

/* 代码块样式 */
.code-block-wrapper {
  margin: 15px 0;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.code-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #2d2d2d;
  color: #e6e6e6;
  padding: 8px 16px;
  font-size: 14px;
}

.code-language {
  font-family: monospace;
  font-weight: bold;
}

.copy-button {
  background: transparent;
  border: none;
  color: #e6e6e6;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  transition: all 0.3s;
}

.copy-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.code-block {
  margin: 0;
  padding: 16px;
  background-color: #282c34;
  color: #abb2bf;
  overflow-x: auto;
  font-family: 'Fira Code', Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  tab-size: 4;
}

.code-block code {
  background: transparent;
  padding: 0;
  white-space: pre;
}

/* 全屏加载遮罩样式 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  border-radius: 8px;
}

.loading-content {
  text-align: center;
  padding: 40px;
}

.loading-spinner {
  font-size: 48px;
  color: #409EFF;
  margin-bottom: 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
}

.loading-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.loading-dots span {
  width: 12px;
  height: 12px;
  background: #409EFF;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-tip {
  font-size: 14px;
  color: #909399;
}

/* 确保公式和代码在移动设备上也能正常显示 */
@media screen and (max-width: 768px) {
  .ai-float-icon {
    right: 20px;
    bottom: 20px;
    width: 50px;
    height: 50px;
  }

  .ai-float-icon i {
    font-size: 24px;
  }

  .block-formula, .math-formula, .code-block {
    font-size: 90%;
    padding: 10px 5px;
    overflow-x: auto;
  }

  .message-content {
    max-width: 85%;
  }
}
</style>

<style>
/* 全局样式 */
.ai-chat-dialog .el-dialog__body {
  padding: 10px 20px;
}

.katex {
  font-size: 1.1em !important;
  line-height: 1.5em !important;
}

.katex-display {
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 0;
  margin: 10px 0;
  text-align: center;
}

/* 改进公式容器样式 */
.math-formula, .block-formula {
  background-color: #f9f9f9;
  border-radius: 6px;
  padding: 10px;
  margin: 15px 0;
  overflow-x: auto;
}

.inline-formula {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin: 0 3px;
}

/* 针对MacOS和iOS设备的优化 */
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .katex-display > .katex {
    min-width: min-content;
  }
}

/* 优化分数显示 */
.katex .mfrac .frac-line {
  border-bottom-width: 1px;
  margin: 0 -0.1em;
}

.katex .mfrac .frac-line::after {
  border-bottom-width: 1px;
}

/* 分子分母的间距 */
.katex .mfrac .msupsub .mfrac-numer {
  padding-bottom: 0.1em;
}

.katex .mfrac .msupsub .mfrac-denom {
  padding-top: 0.1em;
}

/* 优化上标下标显示 */
.katex .msupsub {
  text-align: left;
}

/* 改进大型操作符 (如求和、积分) 的显示 */
.katex .mop {
  margin-top: 0.1em;
  margin-bottom: 0.15em;
}

/* 优化矩阵显示 */
.katex .array {
  margin: 0 0.1em;
}

/* 提高公式渲染错误的可见性 */
.render-error {
  color: #f56c6c;
  border: 1px dashed #f56c6c;
  padding: 8px;
  background-color: #fef0f0;
  border-radius: 4px;
  margin: 5px 0;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}

/* 在<style>部分修改或添加以下样式 */
.message-item.ai .message-content.thinking-process {
  background-color: #f8f8f8; /* 更浅的背景色 */
  border-left: 3px solid #909399; /* 更改为灰色边框 */
  font-size: 14px;
  color: #909399; /* 灰色字体 */
  margin-bottom: 10px;
  padding: 12px;
}

.thinking-process-header {
  font-weight: bold;
  color: #909399; /* 灰色标题 */
  margin-bottom: 8px;
}

/* 添加分割线样式 */
.message-item.ai .message-content.thinking-process hr,
.message-item.ai .message-content.thinking-process p > hr {
  border: 0;
  border-top: 1px solid #dcdfe6;
  margin: 15px 0;
}
</style>
